import { useRef, useEffect, useState, useMemo, useCallback } from "react";
import { useTerminalEngine } from "../../hooks/useTerminalEngine";
import CommandInput from "./terminal/CommandInput";

// ─── Terminal line renderer ──────────────────────────────────────────────────
function TerminalLine({ line }) {
  switch (line.kind) {
    case "blank":
      return <div style={{ height: "6px" }} />;
    case "section":
    case "ascii":
      return (
        <div style={{ color: "#334155", fontFamily: "monospace", fontSize: "12px", userSelect: "none" }}>
          {line.text}
        </div>
      );
    case "prompt":
      return (
        <div style={{ fontFamily: "monospace", fontSize: "13px" }}>
          <span style={{ color: "#34d399" }}>analyst@lakeview:~$</span>
          {" "}
          <span style={{ color: "#e2e8f0" }}>{line.cmd}</span>
        </div>
      );
    case "echo":
      return (
        <div style={{ color: "#cbd5e1", fontFamily: "monospace", fontSize: "13px", whiteSpace: "pre-wrap" }}>
          {line.text}
        </div>
      );
    case "warn":
      return (
        <div style={{ color: "#fbbf24", fontFamily: "monospace", fontSize: "13px", whiteSpace: "pre-wrap" }}>
          ⚠ {line.text}
        </div>
      );
    case "mentor":
      return (
        <div style={{
          color: "#67e8f9", fontFamily: "monospace", fontSize: "13px",
          borderLeft: "2px solid #0891b2", paddingLeft: "10px",
          whiteSpace: "pre-wrap",
        }}>
          {line.text}
        </div>
      );
    default:
      return null;
  }
}

// ─── Panel shell ─────────────────────────────────────────────────────────────
function Panel({ label, badge, children, style }) {
  return (
    <div style={{
      background: "#0f172a",
      border: "1px solid #1e293b",
      borderRadius: "6px",
      overflow: "hidden",
      ...style,
    }}>
      <div style={{
        padding: "6px 10px",
        borderBottom: "1px solid #1e293b",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexShrink: 0,
      }}>
        <span style={{
          color: "#64748b",
          fontFamily: "monospace",
          fontSize: "10px",
          fontWeight: "bold",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
        }}>
          {label}
        </span>
        {badge !== undefined && (
          <span style={{ color: "#334155", fontFamily: "monospace", fontSize: "10px" }}>{badge}</span>
        )}
      </div>
      {children}
    </div>
  );
}

// ─── Objectives panel ────────────────────────────────────────────────────────
const OBJ_MARK  = { done: "✓", current: "◆", pending: "○" };
const OBJ_COLOR = { done: "#34d399", current: "#60a5fa", pending: "#334155" };

function ObjectivesPanel({ objectives }) {
  const done = objectives.filter((o) => o.status === "done").length;
  return (
    <Panel label="Objectives" badge={`${done}/${objectives.length}`}>
      <div style={{ padding: "8px 10px", display: "flex", flexDirection: "column", gap: "6px" }}>
        {objectives.map((obj) => (
          <div key={obj.id} style={{ display: "flex", alignItems: "flex-start", gap: "7px" }}>
            <span style={{
              color: OBJ_COLOR[obj.status] ?? "#334155",
              fontFamily: "monospace",
              fontSize: "12px",
              lineHeight: "1.3",
              flexShrink: 0,
              marginTop: "1px",
            }}>
              {OBJ_MARK[obj.status] ?? "○"}
            </span>
            <span style={{
              color: obj.status === "done" ? "#334155" : obj.status === "current" ? "#e2e8f0" : "#475569",
              fontFamily: "monospace",
              fontSize: "11px",
              lineHeight: "1.5",
              textDecoration: obj.status === "done" ? "line-through" : "none",
            }}>
              {obj.label}
            </span>
          </div>
        ))}
      </div>
    </Panel>
  );
}

// ─── Palette panel ───────────────────────────────────────────────────────────
function PalettePanel({ commands, onFill }) {
  const unlocked = commands.filter((c) => !c.locked);
  const locked   = commands.filter((c) => c.locked);
  return (
    <Panel label="Commands" badge={`${unlocked.length} available`}>
      <div style={{ padding: "6px" }}>
        {unlocked.map((cmd) => (
          <button
            key={cmd.name}
            onClick={() => onFill(cmd.syntax)}
            title={cmd.desc}
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              padding: "5px 8px",
              marginBottom: "3px",
              background: "#1e293b",
              border: "1px solid #334155",
              borderRadius: "4px",
              cursor: "pointer",
              textAlign: "left",
            }}
          >
            <span style={{ fontFamily: "monospace", fontSize: "12px", color: "#e2e8f0" }}>{cmd.syntax}</span>
            <span style={{ fontFamily: "monospace", fontSize: "10px", color: "#475569", marginTop: "1px" }}>{cmd.desc}</span>
          </button>
        ))}
        {locked.map((cmd) => (
          <div
            key={cmd.name}
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              padding: "5px 8px",
              marginBottom: "3px",
              background: "#0a0f1a",
              border: "1px solid #1e293b",
              borderRadius: "4px",
              opacity: 0.45,
            }}
          >
            <span style={{ fontFamily: "monospace", fontSize: "12px", color: "#334155" }}>🔒 {cmd.syntax}</span>
            <span style={{ fontFamily: "monospace", fontSize: "10px", color: "#334155", marginTop: "1px" }}>{cmd.desc}</span>
          </div>
        ))}
      </div>
    </Panel>
  );
}

// ─── Topology panel ──────────────────────────────────────────────────────────
const NODE_COLOR = {
  you:         "#22d3ee",
  clean:       "#475569",
  compromised: "#ef4444",
  isolated:    "#94a3b8",
  blocked:     "#f59e0b",
};

// SVG icon drawn at origin (0,0), sized relative to node radius
function NodeIcon({ type, r, color }) {
  const s = r * 0.55; // icon scale
  switch (type) {
    case "firewall":
      // Shield outline
      return (
        <path
          d={`M0,${-s} L${s},${-s * 0.4} L${s},${s * 0.1} Q${s},${s * 0.75} 0,${s} Q${-s},${s * 0.75} ${-s},${s * 0.1} L${-s},${-s * 0.4} Z`}
          fill="none" stroke={color} strokeWidth="1.3"
        />
      );
    case "server":
      // Three stacked bars (server rack)
      return (
        <g>
          {[-0.55, -0.1, 0.35].map((dy, i) => (
            <rect
              key={i}
              x={-s} y={dy * s}
              width={s * 2} height={s * 0.42}
              rx="1"
              fill="none" stroke={color} strokeWidth="1.1"
            />
          ))}
        </g>
      );
    case "analyst":
      // Person silhouette
      return (
        <g>
          <circle cx="0" cy={-s * 0.45} r={s * 0.33} fill="none" stroke={color} strokeWidth="1.2" />
          <path
            d={`M${-s},${s * 0.7} Q${-s * 0.8},${s * 0.05} 0,${s * 0.05} Q${s * 0.8},${s * 0.05} ${s},${s * 0.7}`}
            fill="none" stroke={color} strokeWidth="1.2"
          />
        </g>
      );
    case "workstation":
      // Laptop / desktop monitor
      return (
        <g>
          <rect x={-s} y={-s * 0.7} width={s * 2} height={s * 1.1} rx="1.5" fill="none" stroke={color} strokeWidth="1.1" />
          <line x1={-s * 0.5} y1={s * 0.55} x2={s * 0.5} y2={s * 0.55} stroke={color} strokeWidth="1.1" />
        </g>
      );
    case "monitor":
    default:
      // Display + stand
      return (
        <g>
          <rect x={-s * 0.95} y={-s * 0.65} width={s * 1.9} height={s * 1.05} rx="1.5" fill="none" stroke={color} strokeWidth="1.1" />
          <line x1={0} y1={s * 0.4} x2={0} y2={s * 0.65} stroke={color} strokeWidth="1.1" />
          <line x1={-s * 0.4} y1={s * 0.65} x2={s * 0.4} y2={s * 0.65} stroke={color} strokeWidth="1.1" />
        </g>
      );
  }
}

function TopologyPanel({ nodes, edges, assetStates }) {
  const [tooltip, setTooltip] = useState(null);

  const resolved = nodes.map((n) => ({
    ...n,
    state: assetStates[n.id] ?? n.state ?? "clean",
  }));

  const viewBox = useMemo(() => {
    if (!nodes.length) return "0 0 360 180";
    const xs = nodes.map((n) => n.x);
    const ys = nodes.map((n) => n.y);
    const pad = 36;
    const minX = Math.min(...xs) - pad;
    const minY = Math.min(...ys) - pad;
    const w    = Math.max(...xs) - minX + pad;
    const h    = Math.max(...ys) - minY + pad;
    return `${minX} ${minY} ${w} ${h}`;
  }, [nodes]);

  const nodeById = (id) => resolved.find((n) => n.id === id);
  const R = 14; // node radius

  return (
    <Panel label="Network Topology" badge={`${nodes.length} hosts`}>
      <div style={{ padding: "6px 8px", position: "relative" }}>
        <svg
          viewBox={viewBox}
          style={{ width: "100%", maxHeight: "180px", display: "block" }}
          preserveAspectRatio="xMidYMid meet"
        >
          {/* Edges */}
          {edges.map((edge, i) => {
            const from = nodeById(edge.from);
            const to   = nodeById(edge.to);
            if (!from || !to) return null;
            return (
              <line
                key={i}
                x1={from.x} y1={from.y}
                x2={to.x}   y2={to.y}
                stroke={edge.hot ? "#ef444455" : "#1e293b"}
                strokeWidth={edge.hot ? 1.5 : 1}
                strokeDasharray={edge.hot ? "4 3" : undefined}
              />
            );
          })}

          {/* Nodes */}
          {resolved.map((node) => {
            const color = NODE_COLOR[node.state] ?? "#475569";
            const isIsolated = node.state === "isolated";
            return (
              <g
                key={node.id}
                transform={`translate(${node.x},${node.y})`}
                style={{ cursor: "pointer" }}
                onMouseEnter={(e) => setTooltip({ node, x: e.clientX, y: e.clientY })}
                onMouseLeave={() => setTooltip(null)}
              >
                {/* Outer glow ring for compromised */}
                {node.state === "compromised" && (
                  <circle r={R + 4} fill="none" stroke="#ef444422" strokeWidth="3" />
                )}

                {/* Main circle */}
                <circle
                  r={R}
                  fill={color + "18"}
                  stroke={color}
                  strokeWidth={node.state === "compromised" ? 2 : 1.5}
                />

                {/* Type icon */}
                <NodeIcon type={node.type} r={R} color={color} />

                {/* Isolated cross overlay */}
                {isIsolated && (
                  <>
                    <line x1={-6} y1={-6} x2={6} y2={6} stroke="#ef4444" strokeWidth="1.8" />
                    <line x1={6}  y1={-6} x2={-6} y2={6} stroke="#ef4444" strokeWidth="1.8" />
                  </>
                )}

                {/* Label below */}
                <text
                  textAnchor="middle"
                  y={R + 11}
                  style={{ fontFamily: "monospace", fontSize: "8px", fill: color, userSelect: "none" }}
                >
                  {node.label}
                </text>
              </g>
            );
          })}
        </svg>

        {/* Legend */}
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "4px" }}>
          {[["you", "#22d3ee"], ["clean", "#475569"], ["compromised", "#ef4444"], ["isolated", "#94a3b8"]].map(([s, c]) => (
            <span key={s} style={{ display: "flex", alignItems: "center", gap: "3px", fontFamily: "monospace", fontSize: "9px", color: "#334155" }}>
              <i style={{ display: "inline-block", width: "7px", height: "7px", borderRadius: "50%", background: c, flexShrink: 0 }} />
              {s}
            </span>
          ))}
        </div>
      </div>

      {/* Tooltip — rendered in a portal-like fixed div */}
      {tooltip && (
        <div style={{
          position: "fixed",
          left: tooltip.x + 14,
          top:  tooltip.y - 10,
          background: "#0f172a",
          border: "1px solid #334155",
          borderRadius: "4px",
          padding: "6px 10px",
          zIndex: 9999,
          pointerEvents: "none",
          boxShadow: "0 4px 12px #00000066",
        }}>
          <div style={{ fontFamily: "monospace", fontSize: "12px", color: "#e2e8f0", marginBottom: "2px" }}>{tooltip.node.label}</div>
          {tooltip.node.ip   && <div style={{ fontFamily: "monospace", fontSize: "10px", color: "#475569" }}>{tooltip.node.ip}</div>}
          {tooltip.node.role && <div style={{ fontFamily: "monospace", fontSize: "10px", color: "#475569", marginTop: "1px" }}>{tooltip.node.role}</div>}
          <div style={{ fontFamily: "monospace", fontSize: "10px", color: NODE_COLOR[tooltip.node.state] ?? "#475569", marginTop: "3px", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            {tooltip.node.state}
          </div>
        </div>
      )}
    </Panel>
  );
}

// ─── Log feed panel ──────────────────────────────────────────────────────────
const LOG_COLOR = { INFO: "#475569", WARN: "#f59e0b", ALERT: "#ef4444", CRITICAL: "#dc2626" };

function LogFeedPanel({ events }) {
  const feedRef = useRef(null);
  useEffect(() => {
    if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight;
  }, [events]);

  return (
    <Panel label="Log Feed" badge={events.length} style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>
      <div
        ref={feedRef}
        style={{ flex: 1, overflowY: "auto", padding: "6px 8px", scrollbarWidth: "thin", scrollbarColor: "#1e293b transparent" }}
      >
        {events.length === 0 && (
          <div style={{ fontFamily: "monospace", fontSize: "10px", color: "#334155", textAlign: "center", padding: "8px 0" }}>
            No events
          </div>
        )}
        {events.map((ev) => (
          <div key={ev.id} style={{ borderLeft: `2px solid ${LOG_COLOR[ev.level] ?? "#334155"}`, paddingLeft: "6px", marginBottom: "7px" }}>
            <div style={{ display: "flex", gap: "6px", alignItems: "baseline" }}>
              <span style={{ fontFamily: "monospace", fontSize: "9px", color: LOG_COLOR[ev.level] ?? "#334155", fontWeight: "bold" }}>
                {ev.level}
              </span>
              <span style={{ fontFamily: "monospace", fontSize: "9px", color: "#334155" }}>{ev.source}</span>
            </div>
            <div style={{ fontFamily: "monospace", fontSize: "10px", color: "#64748b", lineHeight: "1.4", marginTop: "1px" }}>
              {ev.message}
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
}

// ─── Intro / briefing card ───────────────────────────────────────────────────
function IntroCard({ header, body, onDismiss }) {
  return (
    <div style={{
      background: "#0f172a",
      border: "1px solid #1d4ed8",
      borderRadius: "6px",
      padding: "10px 14px",
      flexShrink: 0,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "12px" }}>
        <div>
          <div style={{
            color: "#60a5fa",
            fontFamily: "monospace",
            fontSize: "11px",
            fontWeight: "bold",
            textTransform: "uppercase",
            letterSpacing: "0.07em",
            marginBottom: "5px",
          }}>
            ◈ {header}
          </div>
          <div style={{ color: "#94a3b8", fontFamily: "monospace", fontSize: "12px", lineHeight: "1.65" }}>
            {body}
          </div>
        </div>
        <button
          onClick={onDismiss}
          aria-label="Dismiss briefing"
          style={{
            color: "#475569",
            fontFamily: "monospace",
            fontSize: "18px",
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: 0,
            lineHeight: 1,
            flexShrink: 0,
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
}

// ─── Mission complete overlay ────────────────────────────────────────────────
function MissionCompleteOverlay({ onAdvance }) {
  const [countdown, setCountdown] = useState(3);

  useEffect(() => {
    if (countdown <= 0) { onAdvance(); return; }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown, onAdvance]);

  return (
    <div style={{
      position: "absolute", inset: 0,
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      background: "#0a0f1aee",
      borderRadius: "8px",
      zIndex: 50,
    }}>
      <div style={{
        border: "1px solid #34d399",
        borderRadius: "8px",
        padding: "32px 48px",
        textAlign: "center",
        background: "#0f172a",
        boxShadow: "0 0 40px #34d39922",
      }}>
        <div style={{ fontFamily: "monospace", fontSize: "11px", color: "#34d399", letterSpacing: "0.15em", marginBottom: "12px" }}>
          ◈ MISSION COMPLETE
        </div>
        <div style={{ fontFamily: "monospace", fontSize: "22px", color: "#e2e8f0", marginBottom: "8px" }}>
          IT-OT Boundary Secured
        </div>
        <div style={{ fontFamily: "monospace", fontSize: "12px", color: "#64748b", lineHeight: "1.7", maxWidth: "340px" }}>
          The entry-point workstation has been isolated.<br />
          No further lateral movement detected.
        </div>
        <div style={{ marginTop: "24px", fontFamily: "monospace", fontSize: "11px", color: "#334155" }}>
          Continuing in {countdown}…
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function InteractiveTerminal({ step, onComplete }) {
  const inputRef  = useRef(null);
  const outputRef = useRef(null);
  const [showIntro, setShowIntro] = useState(true);
  const [missionComplete, setMissionComplete] = useState(false);

  const handleComplete = useCallback(() => setMissionComplete(true), []);
  const handleAdvance  = useCallback(() => onComplete?.(), [onComplete]);

  const {
    lines,
    history,
    isStreaming,
    paletteCommands,
    submitCommand,
    objectivesList,
    assetStates,
    logFeed,
  } = useTerminalEngine({
    commandSet:          step.commandSet        ?? [],
    objectives:          step.objectives        ?? [],
    completionCondition: step.completionCondition ?? null,
    logEvents:           step.logEvents         ?? [],
    triggeredEvents:     step.triggeredEvents    ?? {},
    onComplete: handleComplete,
  });

  useEffect(() => {
    if (outputRef.current) outputRef.current.scrollTop = outputRef.current.scrollHeight;
  }, [lines]);

  const availableCandidates = paletteCommands.filter((c) => !c.locked).map((c) => c.name);
  const hasTopology   = (step.topology?.nodes?.length ?? 0) > 0;
  const hasObjectives = objectivesList.length > 0;
  const hasLeftPanel  = hasObjectives || paletteCommands.length > 0;
  const hasRightPanel = hasTopology || logFeed.length > 0 || Object.keys(step.triggeredEvents ?? {}).length > 0;

  return (
    <div style={{
      position: "relative",
      display: "flex",
      flexDirection: "column",
      width: "100%",
      height: "100%",
      minHeight: "560px",
      background: "#0a0f1a",
      borderRadius: "8px",
      border: "1px solid #1e293b",
      padding: "12px",
      boxSizing: "border-box",
      gap: "8px",
    }}>
      {missionComplete && <MissionCompleteOverlay onAdvance={handleAdvance} />}
      {/* Briefing card */}
      {showIntro && step.intro && (
        <IntroCard
          header={step.intro.header}
          body={step.intro.body}
          onDismiss={() => setShowIntro(false)}
        />
      )}

      {/* Three-column content area — overflow:hidden prevents right panel spill */}
      <div style={{
        flex: 1,
        display: "flex",
        gap: "10px",
        minHeight: 0,
        overflow: "hidden",
      }}>

        {/* LEFT: Objectives + Palette */}
        {hasLeftPanel && (
          <div style={{
            width: "180px",
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            overflowY: "auto",
            overflowX: "hidden",
            scrollbarWidth: "thin",
            scrollbarColor: "#1e293b transparent",
          }}>
            {hasObjectives && <ObjectivesPanel objectives={objectivesList} />}
            {paletteCommands.length > 0 && (
              <PalettePanel
                commands={paletteCommands}
                onFill={(s) => inputRef.current?.fill(s)}
              />
            )}
          </div>
        )}

        {/* CENTER: Terminal */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, overflow: "hidden" }}>
          {/* Terminal header bar */}
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            marginBottom: "8px",
            paddingBottom: "8px",
            borderBottom: "1px solid #1e293b",
            flexShrink: 0,
          }}>
            <span style={{ color: "#334155", fontFamily: "monospace", fontSize: "11px" }}>
              gridguard-terminal — analyst session
            </span>
          </div>

          {/* Output buffer */}
          <div
            ref={outputRef}
            style={{
              flex: 1,
              overflowY: "auto",
              marginBottom: "8px",
              scrollbarWidth: "thin",
              scrollbarColor: "#1e293b transparent",
            }}
          >
            {lines.map((line, i) => <TerminalLine key={i} line={line} />)}
            {isStreaming && (
              <span style={{ color: "#34d399", fontFamily: "monospace", fontSize: "13px", animation: "blink 1s step-end infinite" }}>
                ▌
              </span>
            )}
          </div>

          {/* Command input */}
          <CommandInput
            ref={inputRef}
            candidates={availableCandidates}
            history={history}
            disabled={isStreaming}
            onSubmit={submitCommand}
          />
        </div>

        {/* RIGHT: Topology + Log feed */}
        {hasRightPanel && (
          <div style={{
            width: "210px",
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            minHeight: 0,
            overflow: "hidden",
          }}>
            {hasTopology && (
              <TopologyPanel
                nodes={step.topology.nodes}
                edges={step.topology.edges ?? []}
                assetStates={assetStates}
              />
            )}
            <LogFeedPanel events={logFeed} />
          </div>
        )}
      </div>

      <style>{`
        @keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }
      `}</style>
    </div>
  );
}
